import { T } from '@territory/tokens';
import { focusRing } from './focusRing.js';

/*
 * The Wedge Brief V2 icon button (apps/wedge/src/prototype/prep-brief-v2-kit.jsx Bv2IconButton), for IconButton
 * variant="wedge-bv2". Verbatim from the prototype so the swap is a visual no-op: box max(size, 40) with a 44px floor,
 * radius 12, 1px line border, card fill, text ink, hover and pressed accent-soft fill, hover lift 1px, 180ms ease.
 * The prototype has no focus ring or disabled look; those use the one core focus ring and the core disabled opacity.
 * Roles read the --wedge-bv2-* roles in products.wedge; outside Wedge each falls back to a base token.
 */

/* Roles the button reads. products.wedge.roles must set each in both themes. */
export const WEDGE_ICON_BUTTON_ROLES = ['card', 'line', 'text', 'acc-soft', 'acc-ink'] as const;
export type WedgeIconButtonRole = (typeof WEDGE_ICON_BUTTON_ROLES)[number];

const role = (name: WedgeIconButtonRole, fallback: string): string => `var(--wedge-bv2-${name}, ${fallback})`;

/* verbatim Bv2IconButton, off-scale */
export const WEDGE_ICON_BUTTON_DEFAULT = 40;
export const WEDGE_ICON_BUTTON_MIN = 44;
export const WEDGE_ICON_BUTTON_GLYPH = 18;
const RADIUS = 12;
const LIFT_PX = 1;
const MS = 180;
/* BV2_EASE */
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
const DISABLED_OPACITY = 0.4;

const SEL = '[data-rv-icon-button][data-rv-variant="wedge-bv2"]';

export const WEDGE_ICON_BUTTON_CSS = `${SEL} { box-sizing: content-box; display: inline-flex; align-items: center; justify-content: center; padding: 0; margin: 0; flex-shrink: 0; border-radius: ${RADIUS}px; border: 1px solid ${role('line', T.separator)}; background: ${role('card', T.surface)}; color: ${role('text', T.label)}; cursor: pointer; transform: none; transition: transform ${MS}ms ${EASE}, background ${MS}ms ${EASE}; }
${SEL}[aria-pressed="true"] { background: ${role('acc-soft', T.accentSoft)}; color: ${role('acc-ink', T.accent)}; }
${SEL}:not([disabled], [aria-disabled="true"]):hover { background: ${role('acc-soft', T.accentSoft)}; transform: translateY(-${LIFT_PX}px); }
${SEL}[disabled], ${SEL}[aria-disabled="true"] { opacity: ${DISABLED_OPACITY}; cursor: not-allowed; }
@media (prefers-reduced-motion: reduce) { ${SEL} { transition: none; } ${SEL}:hover { transform: none; } }
${focusRing(SEL)}`;
